import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  type OnDestroy,
  type OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription, debounceTime, interval, take } from 'rxjs';
import { getUserSpaces } from '../../../server/services/space.service';
import {
  generateId,
  sortCaseInsensitive,
} from '../../../server/services/utils.service';
import type { OrgSpace } from '../../../server/types/org-types';
import {
  placeMidWeight,
  taskDescriptionMaxLength,
  taskTitleMaxLength,
} from '../../../server/types/task-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import {
  generateTaskInboxThreadFocusPath,
  generateTaskThreadFocusPath,
} from '../inbox.service';
import { TagService } from '../tag.service';
import {
  type OrgTaskWithUpdates,
  type TaskItem,
  TaskService,
  sortTaskItemByParentWeight,
} from '../task.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
  standalone: false,
})
export class TaskDetailComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private utilsService = inject(UtilsService);
  taskService = inject(TaskService);
  private tagService = inject(TagService);
  private changeDetector = inject(ChangeDetectorRef);
  private router = inject(Router);
  private location = inject(Location);

  @Input() orgId: string;
  @Input() spaceId: string | undefined;
  @Input() taskId: string;
  @Input() focus: 'thread' | undefined; // description subtasks discussion undefined
  @Input() context: 'inbox' | 'task-detail-page' | undefined;

  space: DeepReadonly<OrgSpace> | undefined;
  userspaceCount = 0;

  task: DeepReadonly<OrgTaskWithUpdates>;

  completed = false;

  maxDescriptionHeight = 400;
  textAreaLineHeight = 22; // #descriptionTextAreaLineHeight
  taskTitleMaxLength = taskTitleMaxLength;
  taskDescriptionMaxLength = taskDescriptionMaxLength;

  showThreadOnly = true;
  private windowResize = new Subject<number>();
  private windowResizeDebounce = this.windowResize.pipe(debounceTime(500));
  @HostListener('window:resize', ['$event']) onResize(event) {
    this.windowResize.next(event.target.innerWidth);
    this.determineShowThreadOnly();
  }

  titleControl = new FormControl('', { nonNullable: true });
  @ViewChild('titleTextArea') titleTextArea: ElementRef<HTMLTextAreaElement>;

  descriptionControl = new FormControl('', { nonNullable: true });
  @ViewChild('descriptionTextArea')
  descriptionTextArea: ElementRef<HTMLTextAreaElement>;
  descriptionTextAreaActive = false;

  children: TaskItem[] = [];

  showDescription = false;
  showChildren = true;
  showDiscussion = false;

  moveCursorToDeepestChildTaskId: string | undefined;
  moveCursorToDeepestChildAtStart = false;

  readonly listId: string = generateId();

  stage: 'current' | 'backlog' | 'complete';
  stageText = '';

  inputLaunchSource: 'title' | 'description' | undefined = undefined;
  storedCursorPosition = -1; // -1 is at the end
  inputType: 'assignedTo' | 'tag' | undefined = undefined;

  assignedToName = '';
  tagsDisplay: { _id: string; title: string }[] = [];

  dueText = '';
  scheduleText = '';

  parentTasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
  parentHeaderSubscriptions = new Subscription();

  taskDetailPath: string;

  subscriptions = new Subscription();

  ngOnInit(): void {
    const { orgId, spaceId, taskId } = this;

    // In narrow mode, focus on thread should show thread
    if (this.focus === 'thread') {
      this.showDescription = false;
      this.showChildren = false;
      this.showDiscussion = true;
    }

    this.determineShowThreadOnly();
    const sub = this.taskService
      .getTaskAsync({ orgId, spaceId, taskId })
      .subscribe((task) => {
        this.spaceId = task.spaceId;
        this.updateFields(task);

        const spaceSub = this.authUserService.userOrgObj.subscribe(
          (userOrgObj) => {
            if (!userOrgObj) return;
            const org = userOrgObj.org;
            const space = org.spaces.find((s) => s._id === this.spaceId);
            if (space) this.space = space;
            this.userspaceCount = getUserSpaces(
              org.spaces,
              userOrgObj.user._id
            ).length;
          }
        );
        this.subscriptions.add(spaceSub);

        const sub2 = this.taskService
          .taskUpdates({ taskId })
          .subscribe((t) => this.updateFields(t));
        this.subscriptions.add(sub2);

        // Children
        this.children = this.taskService
          .getChildren({ orgId, spaceId: this.spaceId, parentId: taskId })
          .filter((t) => this.task.trashedAt || !t.trashedAt) // Remove trashed children if the parent is not trashed (such as in the trash list)
          .map((t) => ({ taskId: t._id, task: t }));
        this.sortChildren();
        this.children.forEach((child) =>
          this.updateChildSubscription(child.taskId)
        );

        // Add new children as they come in
        const newChildrenSub = this.taskService
          .childTaskUpdateFeed({ parentId: taskId })
          .subscribe((children) => {
            children.forEach((child) => {
              if (
                child.parentId === this.task._id &&
                !this.children.find((c) => c.taskId === child._id) &&
                (this.task.trashedAt || !child.trashedAt) // Remove trashed children if the parent is not trashed (such as in the trash list)
              ) {
                this.children.push({ taskId: child._id, task: child });
                this.sortChildren();
                this.updateChildSubscription(child._id);
              }
            });
          });
        this.subscriptions.add(newChildrenSub);

        // Remove tasks (used in removing blank tasks)
        const removeSubscription = this.taskService
          .taskRemoves({ orgId: this.orgId, spaceId: this.spaceId })
          .subscribe((r) => {
            const index = this.children.findIndex((t) => t.taskId === r.taskId);
            if (index !== -1) {
              this.taskService.removeTaskSelection({
                listId: this.listId,
                taskId,
                parentId: this.task._id,
                orphaned: false,
              });
              this.children.splice(index, 1);
            }
          });
        this.subscriptions.add(removeSubscription);

        this.startTitleSubscription();
        this.startDescriptionSubscription();
      });
    this.subscriptions.add(sub);

    const sizeSub = this.windowResizeDebounce.subscribe((size) => {
      this.recalculateTitleHeight();
      this.recalculateDescriptionHeight();
    });
    this.subscriptions.add(sizeSub);

    this.taskDetailPath = this.location.path().split('?')[0];
  }

  private determineShowThreadOnly() {
    if (window.innerWidth < 768 && this.focus === 'thread')
      this.showThreadOnly = true;
    else this.showThreadOnly = false;
  }

  // Checkbox
  clickCheck() {
    if (this.completed !== !!this.task.completedAt) {
      const completedAt = this.completed ? Date.now() : null;
      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.updateTask({ orgId, spaceId, taskId }, { completedAt });
    }
  }

  clickAddDescriptionButton() {
    this.showDescription = true;
    this.showChildren = false;
    this.deselectAllChildren();
    this.showDiscussion = false;
  }

  clickDescription() {
    this.showDescription = !this.showDescription;
    this.showChildren = false;
    this.deselectAllChildren();
    this.showDiscussion = false;
    this.recalculateDescriptionHeight();
  }

  clickSubtasks() {
    this.showChildren = !this.showChildren;
    if (!this.showChildren) this.deselectAllChildren();
    this.showDescription = false;
    this.showDiscussion = false;
  }

  clickDiscussion() {
    if (window.innerWidth <= 768) {
      const { orgId, taskId } = this;
      let path: string;
      if (this.context === 'inbox') {
        path = generateTaskInboxThreadFocusPath({ orgId, taskId });
      } else {
        path = generateTaskThreadFocusPath({ orgId, taskId });
      }
      this.router.navigate([path]);
    } else {
      this.showDiscussion = !this.showDiscussion;
      this.showDescription = false;
      this.showChildren = false;
    }
  }

  // Title
  private startTitleSubscription() {
    const sub = this.titleControl.valueChanges.subscribe((value) => {
      if (value === this.task.title) return;

      if (value.includes('\n')) {
        value = value.replace('\n', '');
        this.titleControl.setValue(value);
      }

      this.recalculateTitleHeight();

      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.updateTitleWait({
        orgId,
        spaceId,
        taskId,
        title: value,
      });
    });
    this.subscriptions.add(sub);
  }

  private recalculateTitleHeight() {
    const sub = interval(10)
      .pipe(take(1))
      .subscribe(() => {
        const nativeTitle = this.titleTextArea?.nativeElement;
        if (!nativeTitle) return;
        nativeTitle.setAttribute('style', 'height: 0px');
        nativeTitle.setAttribute(
          'style',
          'height:' + nativeTitle.scrollHeight + 'px'
        );
      });
    this.subscriptions.add(sub);
  }

  onTitleTextareaFocus(event: FocusEvent): void {
    this.titleTextArea.nativeElement.spellcheck = true;

    // this.menuService.mobileToolbarHide();
  }

  onTitleTextareaBlur(event: FocusEvent): void {
    this.saveTitle();
    // Lower down to prevent errors affecting code above
    this.titleTextArea.nativeElement.spellcheck = false;
  }

  saveTitle() {
    if (!this.task) return;

    const title = this.titleControl.value;
    const { orgId, spaceId, _id: taskId } = this.task;

    if (title !== this.task.title)
      this.taskService.updateTask({ orgId, spaceId, taskId }, { title });
  }

  // Description
  private startDescriptionSubscription() {
    const sub = this.descriptionControl.valueChanges.subscribe((value) => {
      if (value === this.task.description) return;

      this.recalculateDescriptionHeight();

      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.updateDescriptionWait({
        orgId,
        spaceId,
        taskId,
        description: value,
      });

      // TODO: back off after doing nothing for 3 seconds then save or if closing the task save straight away
    });
    this.subscriptions.add(sub);
  }

  private recalculateDescriptionHeight() {
    const sub = interval(1)
      .pipe(take(1))
      .subscribe(() => {
        const nativeDescription = this.descriptionTextArea.nativeElement;
        if (!nativeDescription) return;
        nativeDescription.setAttribute('style', 'height: 0px');
        // Adding an extra line plus a bit is a hack to prevent flashing
        let height =
          nativeDescription.scrollHeight < this.textAreaLineHeight
            ? this.textAreaLineHeight + this.textAreaLineHeight + 5
            : nativeDescription.scrollHeight + this.textAreaLineHeight + 5;
        if (height > this.maxDescriptionHeight)
          height = this.maxDescriptionHeight;
        nativeDescription.setAttribute('style', 'height:' + height + 'px');
      });
    this.subscriptions.add(sub);
  }

  onDescriptionTextareaFocus(event: FocusEvent): void {
    this.descriptionTextArea.nativeElement.spellcheck = true;
  }

  onDescriptionTextareaBlur(event: FocusEvent): void {
    this.attemptDescriptionUpdate();
    // Lower down to prevent errors affecting code above
    this.descriptionTextArea.nativeElement.spellcheck = false;
  }

  attemptDescriptionUpdate() {
    if (!this.task) return;

    const description = this.descriptionControl.value;
    const { orgId, spaceId, _id: taskId } = this.task;

    if (description !== this.task.description)
      this.taskService.updateTask({ orgId, spaceId, taskId }, { description });
  }

  // This is a method for checking whether this child has been removed from the
  // list or if it has been repositioned.
  private updateChildSubscription(taskId: string) {
    let sub: Subscription | undefined;
    sub = this.taskService.taskUpdates({ taskId }).subscribe((updatedChild) => {
      const index = this.children.findIndex((c) => c.task._id === taskId);
      // Remove if the child has been deleted and the parent is not deleted (such as in the trash list)
      if (
        this.task &&
        index !== -1 &&
        (updatedChild.parentId !== this.task._id ||
          (!this.task.trashedAt && updatedChild.trashedAt))
      ) {
        // Remove the child from the list as the parent no longer matches
        this.taskService.removeTaskSelection({
          listId: this.listId,
          taskId,
          parentId: this.task._id,
          orphaned: false,
        });
        this.children.splice(index, 1);
        this.sortChildren();
        // We can remove this subscription since it has been removed
        sub?.unsubscribe();
        sub = undefined;
        return;
      }

      if (index >= 0) {
        this.children[index].task = updatedChild;
        this.sortChildren();
      }
    });
    this.subscriptions.add(sub);
  }

  private sortChildren() {
    this.children.sort(sortTaskItemByParentWeight);
  }

  private updateFields(task: DeepReadonly<OrgTaskWithUpdates>) {
    this.task = task;

    if (this.titleControl.value !== task.title)
      this.titleControl.setValue(task.title);

    if (this.descriptionControl.value !== task.description)
      this.descriptionControl.setValue(task.description);

    this.completed = !!task.completedAt;

    this.stage = task.completedAt
      ? 'complete'
      : task.currentAt
      ? 'current'
      : 'backlog';
    this.stageText =
      this.stage === 'complete'
        ? 'Complete'
        : this.stage === 'current'
        ? 'Current'
        : this.stage === 'backlog'
        ? 'Backlog'
        : '';

    // Show user
    if (task.assignedTo) {
      this.assignedToName =
        this.authUserService.getUser({
          orgId: this.orgId,
          userId: task.assignedTo,
        })?.name ?? 'Not found';
    } else {
      this.assignedToName = '';
    }

    // Get tags
    const { orgId, spaceId } = this;
    this.task.tagIds.forEach((tagId) => {
      let sub: Subscription | undefined = undefined;
      if (!spaceId) throw new Error('spaceId not set when it was expected');
      sub = this.tagService.getTag({ orgId, spaceId, tagId }).subscribe({
        next: (tag) => {
          const existing = this.tagsDisplay.find((t) => t._id === tag._id);
          if (existing) {
            existing.title = tag.name;
          } else {
            this.tagsDisplay.push({ _id: tag._id, title: tag.name });
          }
          this.tagsDisplay.sort((a, b) =>
            sortCaseInsensitive(a.title, b.title)
          );
          sub?.unsubscribe();
        },
        error: () => {
          sub?.unsubscribe();
        },
      });
      this.subscriptions.add(sub);
    });

    // Format dueAt
    if (task.dueAt) {
      this.dueText = this.utilsService.shortDateTimeFormat({
        time: task.dueAt,
        veryShort: false,
      });
    } else {
      this.dueText = '';
    }

    // Format schedule
    if (task.schedule) {
      this.scheduleText = this.utilsService.shortDateTimeRangeFormat({
        ...task.schedule,
        veryShort: false,
      });
    } else {
      this.scheduleText = '';
    }

    this.getParents();

    this.recalculateTitleHeight();
    this.recalculateDescriptionHeight();
  }

  setCurrent() {
    if (this.task && (!this.task.currentAt || this.task.completedAt))
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { currentAt: Date.now(), completedAt: null }
      );
  }

  setBacklog() {
    if (this.task && (this.task.currentAt || this.task.completedAt))
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { currentAt: null, completedAt: null }
      );
  }

  setComplete() {
    if (this.task && !this.task.completedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { completedAt: Date.now() }
      );
  }

  trashTask() {
    const { orgId, spaceId, taskId } = this;
    this.taskService.updateTask(
      { orgId, spaceId, taskId },
      { trashedAt: Date.now() }
    );
  }

  removeFromTrash() {
    if (this.task && this.task.trashedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { trashedAt: null }
      );
  }

  getParents() {
    this.parentHeaderSubscriptions.unsubscribe();

    this.parentHeaderSubscriptions = new Subscription();
    this.subscriptions.add(this.parentHeaderSubscriptions);

    const { orgId, spaceId, taskId } = this;
    if (!spaceId) throw new Error('spaceId not set when it was expected');
    this.parentTasks = this.taskService.getParents({ orgId, spaceId, taskId });

    // If there is any change in the parents, just get them all again
    this.parentTasks.map((p) => {
      const sub = this.taskService
        .taskUpdates({ taskId: p._id })
        .subscribe(() => this.getParents());

      this.subscriptions.add(sub);
      this.parentHeaderSubscriptions.add(sub);
    });
  }

  moveCursorToPrior(atStart: boolean, priorChild?: TaskItem) {
    if (priorChild) {
      this.moveCursorToDeepestChildTaskId = undefined;
      // We setTimeout here to force an update
      setTimeout(() => {
        this.moveCursorToDeepestChildAtStart = atStart;
        this.moveCursorToDeepestChildTaskId = priorChild.taskId;
      }, 10);
    }
  }

  moveCursorToNext(atStart: boolean, nextChild?: TaskItem) {
    if (nextChild) {
      const taskId = nextChild.taskId;
      const { listId } = this;
      this.taskService.setTaskCursor({
        taskId,
        listId,
        start: atStart ? 0 : undefined,
      });
    }
  }

  specialKeyPressInterceptTitle(event: KeyboardEvent): void {
    const { orgId, spaceId, _id: taskId } = this.task;

    // Enter
    if (event.key === 'Enter') {
      // Toggle completedAt
      if (this.utilsService.ctrlOrCmd(event)) {
        event.preventDefault();
        const completedAt = this.task.completedAt ? null : Date.now();
        this.taskService.updateTask(
          { orgId, spaceId, taskId },
          { completedAt }
        );
        return;
      }

      event.preventDefault();

      if (this.titleControl.value === '') {
        // Do nothing as we can't set an existing task to have no title, and can't add a new entry below
      } else {
        // Submit
        this.saveTitle();
      }
    }

    // @
    if (event.key === '@') {
      event.preventDefault();
      this.openInputFromTitle('assignedTo');
    }

    // #
    if (event.key === '#') {
      event.preventDefault();
      this.openInputFromTitle('tag');
    }
  }

  specialKeyPressInterceptDescription(event: KeyboardEvent): void {
    // @
    if (event.key === '@') {
      event.preventDefault();
      this.openInputFromDescription('assignedTo');
    }

    // #
    if (event.key === '#') {
      event.preventDefault();
      this.openInputFromDescription('tag');
    }
  }

  openInput(inputType: 'assignedTo' | 'tag') {
    this.inputLaunchSource = undefined;
    this.inputType = inputType; // Launches input
  }

  openInputFromTitle(inputType: 'assignedTo' | 'tag') {
    this.inputLaunchSource = 'title';
    this.storedCursorPosition = this.titleTextArea.nativeElement.selectionEnd;
    this.inputType = inputType; // Launches input
  }

  openInputFromDescription(inputType: 'assignedTo' | 'tag') {
    this.inputLaunchSource = 'description';
    this.storedCursorPosition =
      this.descriptionTextArea.nativeElement.selectionEnd;
    this.inputType = inputType; // Launches input
  }

  returnToCursor(position: 'end' | undefined) {
    this.inputType = undefined;

    if (!this.inputLaunchSource) return;

    let element: HTMLTextAreaElement;
    if (this.inputLaunchSource === 'description') {
      if (!this.descriptionTextAreaActive) {
        this.descriptionTextAreaActive = true;
        this.changeDetector.detectChanges();
      }
      element = this.descriptionTextArea.nativeElement;
    } else if (this.inputLaunchSource === 'title') {
      element = this.titleTextArea.nativeElement;
    } else {
      throw new Error('unknown state');
    }

    setTimeout(() => {
      element.focus();
      const pos = position === 'end' ? 10000 : this.storedCursorPosition;
      element.setSelectionRange(pos, pos);
    }, 50);
  }

  typeCharacter(character: string) {
    this.inputType = undefined;

    let element: HTMLTextAreaElement;
    let control: FormControl<string>;
    if (this.inputLaunchSource === 'description') {
      if (!this.descriptionTextAreaActive) {
        this.descriptionTextAreaActive = true;
        this.changeDetector.detectChanges();
      }
      element = this.descriptionTextArea.nativeElement;
      control = this.descriptionControl;
    } else if (this.inputLaunchSource === 'title') {
      element = this.titleTextArea.nativeElement;
      control = this.titleControl;
    } else {
      throw new Error('unknown state');
    }

    const current = control.value;
    let insertAtPos = this.storedCursorPosition;
    if (insertAtPos === -1) insertAtPos = current.length;

    const newValue = [
      current.slice(0, insertAtPos),
      character,
      current.slice(insertAtPos),
    ].join('');
    control.setValue(newValue);
    const i = insertAtPos + 1;
    element.focus();
    element.setSelectionRange(i, i);
  }

  closeTaskInput() {
    this.inputType = undefined;
  }

  addNewTaskAtEnd() {
    const userOrgObj = this.authUserService.getAuthUserOrgObj({});

    const lastTask = this.children[this.children.length - 1];
    const parentWeight = placeMidWeight(lastTask?.task.parentWeight, undefined);
    const { spaceId } = this;
    if (!spaceId) throw new Error('spaceId not set when it was expected');
    const task = this.taskService.createTask({
      orgId: this.orgId,
      spaceId,
      createdBy: userOrgObj.user._id,
      currentAt: this.task.currentAt ? Date.now() : undefined,
      tagIds: [...this.task.tagIds],
      assignedTo: this.task.assignedTo,
      weight: parentWeight,
      parentId: this.task._id,
      parentWeight,
      autoDeletable: true,
    });
    this.taskService.setTaskCursor({ taskId: task._id, listId: this.listId });
  }

  clickAnywhereOnDetail() {
    if (this.task.autoDeletable) {
      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.stopAutoDelete({ orgId, spaceId, taskId });
    }
  }

  deselectAllChildren() {
    this.children.forEach((child) =>
      this.taskService.removeTaskSelection({
        listId: this.listId,
        taskId: child.taskId,
        parentId: this.taskId,
        orphaned: false,
      })
    );
  }

  ngOnDestroy(): void {
    // Update if needed
    try {
      if (this.task) {
        const { orgId, spaceId, _id: taskId } = this.task;
        let update: { title?: string; description?: string } | undefined =
          undefined;
        if (this.task.title !== this.titleControl.value)
          update = { title: this.titleControl.value };

        if (this.task.description !== this.descriptionControl.value) {
          if (!update) update = {};
          update.description = this.descriptionControl.value;
        }

        if (update)
          this.taskService.updateTask({ orgId, spaceId, taskId }, update);
      }
    } catch (err) {
      console.error(err);
    }
    this.subscriptions.unsubscribe();

    this.deselectAllChildren();
  }
}
